import { inject } from '@angular/core';
import {
  signalStore,
  type,
  withState,
  withMethods,
  patchState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  pipe,
  switchMap,
  EMPTY,
  tap,
  concatMap,
  mergeMap,
  filter,
  from,
} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorStore } from '@application/errors/data-access';
import {
  setPropError,
  setPropLoaded,
  setPropLoading,
  withReqState,
  setPropInit,
} from '@application/data-access';
import {
  addEntity,
  entityConfig,
  removeEntity,
  setEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import {
  DependentType,
  TAccessTime,
  TCondition,
  TFinancialRuleSet,
  TProduct,
  TFinancialRule,
  TAdjustmentRule,
  TFinancialCancellationRule,
  TPlan,
  TPartialPlan,
  TPlanAccessTimeOccupancy,
} from '../entities/subscription-settings.model';
import { SubscriptionService } from '../infra/subscription.service';
import { tapResponse } from '@ngrx/operators';
import { DxOdataStoreService } from '@shared/components/dx-data-grid/data-access';
import { APP_CONFIG } from '@application/config';
import {
  SelfUserStore,
  PermissionNumber,
} from '@domains/person/user/self/data';
import { DxPermissionGuardConfig } from '@shared/components/dx-data-grid/data-access';
import { TSpecialCondition } from '../entities/special-condition.model';

const TEMP_AUTH_PERMISSION_GUARD_PATH = 'TEMPORARY_AUTH_MODAL.PERMISSION_GUARD';

/**
 * Mantém `plans` (opções dos seletores) coerente após mutar um plano.
 * Se a lista ainda não foi carregada, não faz nada — o próximo `loadAllPlans`
 * busca tudo do servidor.
 */
function upsertPlanOption(
  plans: TPartialPlan[],
  plan: Partial<TPlan> | null | undefined
): TPartialPlan[] {
  if (plans.length === 0 || !plan?.PlanId) {
    return plans;
  }

  const option: TPartialPlan = {
    PlanId: plan.PlanId,
    Name: plan.Name ?? '',
    IsActive: plan.IsActive ?? true,
  };
  const index = plans.findIndex((item) => item.PlanId === plan.PlanId);

  if (index === -1) {
    return [...plans, option];
  }

  const updated = [...plans];
  updated[index] = option;
  return updated;
}

export type TSubscriptionSettingsStore = {
  accessTime: TAccessTime | null;
  condition: TCondition | null;
  currentFinancialRuleSet: TFinancialRuleSet | null;
  currentAccessTime: TAccessTime | null;
  currentProduct: TProduct | null;
  currentPlan: TPlan | null;
  financialRule: TFinancialRuleSet | null;
  specialCondition: TSpecialCondition | null;
  plan: TPlan | null;
  /** Lista resumida de planos usada nos seletores (OPTIONS /Plan). */
  plans: TPartialPlan[];
  /**
   * Ocupação dos horários de `accessTimesOccupancyPlanId`. Só ajuda visual: a
   * API confere as vagas de qualquer forma ao gravar a assinatura.
   */
  accessTimesOccupancy: TPlanAccessTimeOccupancy[];
  accessTimesOccupancyPlanId: string | null;
  showSpecialConditionModal: boolean;
};

export const initialSubscriptionSettingsStore: TSubscriptionSettingsStore = {
  accessTime: null,
  condition: null,
  currentFinancialRuleSet: null,
  currentAccessTime: null,
  currentProduct: null,
  currentPlan: null,
  financialRule: null,
  specialCondition: null,
  plan: null,
  plans: [],
  accessTimesOccupancy: [],
  accessTimesOccupancyPlanId: null,
  showSpecialConditionModal: false,
};

const configAccessTime = entityConfig({
  entity: type<TAccessTime>(),
  collection: 'accessTimes',
  selectId: (accessTime: TAccessTime) => accessTime.AccessTimeId,
});

const configProduct = entityConfig({
  entity: type<TProduct>(),
  collection: 'products',
  selectId: (product: TProduct) => product.ProductId,
});

const configCondition = entityConfig({
  entity: type<TCondition>(),
  collection: 'conditions',
  selectId: (condition: TCondition) => condition.ConditionId,
});

const configFinancialRuleSet = entityConfig({
  entity: type<TFinancialRuleSet>(),
  collection: 'financialRuleSets',
  selectId: (financialRuleSet: TFinancialRuleSet) =>
    financialRuleSet.FinancialRuleSetId,
});

const configSpecialCondition = entityConfig({
  entity: type<TSpecialCondition>(),
  collection: 'specialConditions',
  selectId: (specialCondition: TSpecialCondition) =>
    specialCondition.SpecialConditionId,
});

export const SubscriptionSettingsStore = signalStore(
  { providedIn: 'root' },
  withState<TSubscriptionSettingsStore>(initialSubscriptionSettingsStore),
  withEntities(configAccessTime),
  withEntities(configProduct),
  withEntities(configCondition),
  withEntities(configFinancialRuleSet),
  withEntities(configSpecialCondition),
  withMethods(
    (
      store,
      subscriptionService = inject(SubscriptionService),
      errorStore = inject(ErrorStore),
      dxOdataStoreService = inject(DxOdataStoreService),
      appConfig = inject(APP_CONFIG),
      selfUserStore = inject(SelfUserStore)
    ) => ({
      getPlanDataSource: () => {
        return dxOdataStoreService.fromOdata({
          url: appConfig.apiUrl.odata_subscription_service,
          metadata: 'Plans',
          key: 'PlanId',
          params: {
            $expand: 'Resources($expand=ResourcesRanges)',
          },
          permissionGuard: {
            permissions: [PermissionNumber.AssinaturaObterTodos],
            translationKey: 'SUBSCRIPTION_LIST',
          },
        });
      },

      getAccessTimeDataSource: () => {
        return dxOdataStoreService.fromOdata<TAccessTime>({
          url: appConfig.apiUrl.odata_subscription_service,
          metadata: 'AccessTimes',
          key: 'AccessTimeId',
          permissionGuard: {
            permissions: [PermissionNumber.AssinaturaHorarioObterTodos],
            translationKey: 'SUBSCRIPTION_SCHEDULE_LIST',
          },
        });
      },

      getFinancialRuleSetsDataSource: () => {
        return dxOdataStoreService.fromOdata<TFinancialRuleSet>({
          url: appConfig.apiUrl.odata_subscription_service,
          metadata: 'FinancialRuleSets',
          key: 'FinancialRuleSetId',
        });
      },

      getSpecialConditionsDataSource: () => {
        return dxOdataStoreService.fromOdata<TSpecialCondition>({
          url: appConfig.apiUrl.odata_subscription_service,
          metadata: 'SpecialConditions',
          key: 'SpecialConditionId',
        });
      },

      /**
       * Lista resumida de planos para seletores. Reaproveita o que já está em
       * memória; passe `true` para forçar refetch.
       */
      loadAllPlans: rxMethod<boolean | undefined | null | void>(
        pipe(
          switchMap((refresh) => {
            patchState(store, setPropLoading('loadAllPlans'));

            if (store.plans().length > 0 && !refresh) {
              patchState(store, setPropLoaded('loadAllPlans'));
              return EMPTY;
            }

            return subscriptionService.getAllPlans().pipe(
              tapResponse({
                next: (response: TPartialPlan[]) => {
                  patchState(store, {
                    plans: response ?? [],
                    ...setPropLoaded('loadAllPlans'),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadAllPlans', error));
                },
              })
            );
          })
        )
      ),

      /**
       * Carrega o plano para os fluxos de assinatura da pessoa
       * (subscription-create). Ao contrário de `loadPlan`, NÃO exige
       * `AssinaturaObterPorId`: essa é a permissão do catálogo de planos
       * (Settings), não deste fluxo, que é governado pelas permissões
       * `AssinaturaPessoa*`. Ver PR #2220.
       */
      loadPlanForSubscription: rxMethod<string>(
        pipe(
          tap(() =>
            patchState(store, setPropLoading('loadPlanForSubscription'))
          ),
          switchMap((id: string) =>
            subscriptionService.getPlanById(id).pipe(
              tapResponse({
                next: (response: TPlan) => {
                  patchState(store, {
                    currentPlan: response,
                    ...setPropLoaded('loadPlanForSubscription'),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(
                    store,
                    setPropError('loadPlanForSubscription', error)
                  );
                },
              })
            )
          )
        )
      ),

      /**
       * Vagas ocupadas/disponíveis de cada horário do plano. Falha em silêncio:
       * a contagem é só ajuda visual na venda e no cadastro, e a gravação da
       * assinatura recusa por falta de vaga com a mensagem do servidor.
       */
      loadAccessTimesOccupancy: rxMethod<string>(
        pipe(
          tap((planId: string) =>
            patchState(store, {
              accessTimesOccupancy: [],
              accessTimesOccupancyPlanId: planId,
              ...setPropLoading('loadAccessTimesOccupancy'),
            })
          ),
          switchMap((planId: string) =>
            subscriptionService.getAccessTimesOccupancy(planId).pipe(
              tapResponse({
                next: (response: TPlanAccessTimeOccupancy[]) => {
                  patchState(store, {
                    accessTimesOccupancy: response ?? [],
                    ...setPropLoaded('loadAccessTimesOccupancy'),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  patchState(
                    store,
                    setPropError('loadAccessTimesOccupancy', error)
                  );
                },
              })
            )
          )
        )
      ),

      clearAccessTimesOccupancy: () => {
        patchState(store, {
          accessTimesOccupancy: [],
          accessTimesOccupancyPlanId: null,
          ...setPropInit('loadAccessTimesOccupancy'),
        });
      },

      loadPlan: rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadPlan'))),
          switchMap((id: string) =>
            from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaObterPorId],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_VIEW.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_VIEW.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(
                    store,
                    setPropError('loadPlan', new Error('ALERT.ERROR_401'))
                  );
                  return EMPTY;
                }

                return subscriptionService.getPlanById(id).pipe(
                  tapResponse({
                    next: (response: TPlan) => {
                      patchState(store, {
                        currentPlan: response,
                        ...setPropLoaded('loadPlan'),
                      });
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                      patchState(store, setPropError('loadPlan', error));
                    },
                  })
                );
              })
            )
          )
        )
      ),

      addPlan: rxMethod<Partial<TPlan>>(
        pipe(
          tap(() => patchState(store, setPropLoading('addPlan'))),
          switchMap((payload: Partial<TPlan>) =>
            from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaAdicionar],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_CREATE.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_CREATE.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(
                    store,
                    setPropError('addPlan', new Error('ALERT.ERROR_401'))
                  );
                  return EMPTY;
                }

                const normalized = normalizePlanPayload(
                  structuredClone(payload),
                  'add'
                );
                return subscriptionService.addPlan(normalized).pipe(
                  tapResponse({
                    next: (response: TPlan) => {
                      patchState(store, {
                        plan: response,
                        plans: upsertPlanOption(store.plans(), response),
                        ...setPropLoaded('addPlan'),
                      });
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                      patchState(store, setPropError('addPlan', error));
                    },
                  })
                );
              })
            )
          )
        )
      ),

      updatePlan: rxMethod<Partial<TPlan>>(
        pipe(
          tap(() => patchState(store, setPropLoading('updatePlan'))),
          switchMap((payload: Partial<TPlan>) =>
            from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaAtualizar],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_UPDATE.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_UPDATE.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(
                    store,
                    setPropError('updatePlan', new Error('ALERT.ERROR_401'))
                  );
                  return EMPTY;
                }

                const normalized = normalizePlanPayload(
                  structuredClone(payload),
                  'update'
                );
                return subscriptionService.updatePlan(normalized).pipe(
                  tapResponse({
                    next: (response: TPlan) => {
                      patchState(store, {
                        plans: upsertPlanOption(
                          store.plans(),
                          response ?? normalized
                        ),
                        ...setPropLoaded('updatePlan'),
                      });
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                      patchState(store, setPropError('updatePlan', error));
                    },
                  })
                );
              })
            )
          )
        )
      ),

      removePlan: rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('removePlan'))),
          switchMap((id: string) =>
            from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaRemover],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_DELETE.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_DELETE.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(
                    store,
                    setPropError('removePlan', new Error('ALERT.ERROR_401'))
                  );
                  return EMPTY;
                }

                return subscriptionService.removePlan(id).pipe(
                  tapResponse({
                    next: () => {
                      patchState(store, {
                        plans: store
                          .plans()
                          .filter((option) => option.PlanId !== id),
                        ...setPropLoaded('removePlan'),
                      });
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                      patchState(store, setPropError('removePlan', error));
                    },
                  })
                );
              })
            )
          )
        )
      ),

      resetPlanReqState: () => {
        patchState(
          store,
          setPropInit('loadPlan'),
          setPropInit('addPlan'),
          setPropInit('updatePlan'),
          setPropInit('removePlan')
        );
      },

      clearCurrentPlan: () => {
        patchState(store, { currentPlan: null });
      },

      clearCurrentSpecialCondition: () => {
        patchState(store, { specialCondition: null });
      },

      /**
       * Blanks the special-condition slice (state + request flags). Called on
       * leaving the route so stale `*Loaded` flags don't re-trigger the success
       * alerts when the section is re-mounted.
       */
      blankSpecialConditionState: () => {
        patchState(store, {
          specialCondition: null,
          showSpecialConditionModal: false,
          ...setPropInit(
            'loadSpecialCondition',
            'addSpecialCondition',
            'updateSpecialCondition',
            'removeSpecialCondition'
          ),
        });
      },

      setPropsInit(props: string[]): void {
        patchState(store, { ...setPropInit(...props) });
      },

      addAccessTime: rxMethod<Partial<TAccessTime>>(
        pipe(
          tap(() => patchState(store, setPropLoading('addAccessTime'))),
          switchMap((newAccessTime: Partial<TAccessTime>) =>
            from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaHorarioAdicionar],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_SCHEDULE_CREATE.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_SCHEDULE_CREATE.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(
                    store,
                    setPropError('addAccessTime', new Error('ALERT.ERROR_401'))
                  );
                  return EMPTY;
                }

                return subscriptionService.addAccessTime(newAccessTime).pipe(
                  tapResponse({
                    next: (response: TAccessTime) => {
                      patchState(store, {
                        accessTime: response,
                        ...setPropLoaded('addAccessTime'),
                      });
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                      patchState(store, setPropError('addAccessTime', error));
                    },
                  })
                );
              })
            )
          )
        )
      ),

      updateAccessTime: rxMethod<Partial<TAccessTime>>(
        pipe(
          tap(() => patchState(store, setPropLoading('updateAccessTime'))),
          switchMap((accessTimeData: Partial<TAccessTime>) =>
            from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaHorarioAtualizar],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_SCHEDULE_UPDATE.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_SCHEDULE_UPDATE.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(
                    store,
                    setPropError(
                      'updateAccessTime',
                      new Error('ALERT.ERROR_401')
                    )
                  );
                  return EMPTY;
                }

                return subscriptionService
                  .updateAccessTime(accessTimeData)
                  .pipe(
                    tapResponse({
                      next: (response: TAccessTime) => {
                        patchState(
                          store,
                          updateEntity(
                            { id: response.AccessTimeId, changes: response },
                            configAccessTime
                          ),
                          setPropLoaded('updateAccessTime')
                        );
                      },
                      error: (error: HttpErrorResponse) => {
                        patchState(
                          store,
                          setPropError('updateAccessTime', error)
                        );
                      },
                    })
                  );
              })
            )
          )
        )
      ),

      removeAccessTime: rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('removeAccessTime'))),
          switchMap((id: string) =>
            from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaHorarioRemover],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_SCHEDULE_DELETE.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_SCHEDULE_DELETE.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(
                    store,
                    setPropError(
                      'removeAccessTime',
                      new Error('ALERT.ERROR_401')
                    )
                  );
                  return EMPTY;
                }

                return subscriptionService.removeAccessTime(id).pipe(
                  tapResponse({
                    next: () => {
                      patchState(
                        store,
                        removeEntity(id, configAccessTime),
                        setPropLoaded('removeAccessTime')
                      );
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                      patchState(
                        store,
                        setPropError('removeAccessTime', error)
                      );
                    },
                  })
                );
              })
            )
          )
        )
      ),

      loadOneAccessTime: rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadOneAccessTime'))),
          mergeMap((id: string) =>
            from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaHorarioObterPorId],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_SCHEDULE_VIEW.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_SCHEDULE_VIEW.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(
                    store,
                    setPropError(
                      'loadOneAccessTime',
                      new Error('ALERT.ERROR_401')
                    )
                  );
                  return EMPTY;
                }

                return subscriptionService.getAccessTimeById(id).pipe(
                  tapResponse({
                    next: (response: TAccessTime) => {
                      patchState(
                        store,
                        addEntity(response, configAccessTime),
                        { currentAccessTime: response },
                        setPropLoaded('loadOneAccessTime')
                      );
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                      patchState(
                        store,
                        setPropError('loadOneAccessTime', error)
                      );
                    },
                  })
                );
              })
            )
          )
        )
      ),

      loadAccessTime: rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadAccessTime'))),
          switchMap((id: string) =>
            from(
              selfUserStore.ensurePermissions(
                [PermissionNumber.AssinaturaHorarioObterPorId],
                {
                  title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_SCHEDULE_VIEW.TITLE`,
                  message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.SUBSCRIPTION_SCHEDULE_VIEW.MESSAGE`,
                }
              )
            ).pipe(
              switchMap((hasPermission) => {
                if (!hasPermission) {
                  patchState(
                    store,
                    setPropError('loadAccessTime', new Error('ALERT.ERROR_401'))
                  );
                  return EMPTY;
                }

                return subscriptionService.getAccessTimeById(id).pipe(
                  tapResponse({
                    next: (response: TAccessTime) => {
                      patchState(store, {
                        currentAccessTime: response,
                        ...setPropLoaded('loadAccessTime'),
                      });
                    },
                    error: (error: HttpErrorResponse) => {
                      errorStore.setHttpError(error);
                      patchState(store, setPropError('loadAccessTime', error));
                    },
                  })
                );
              })
            )
          )
        )
      ),

      loadAllProducts: rxMethod<boolean | undefined | null | void>(
        pipe(
          switchMap((refresh) => {
            patchState(store, setPropLoading('loadAllProducts'));
            const products = structuredClone(store?.productsEntities());
            const hasProducts = products?.length > 0;

            return subscriptionService.getAllProducts().pipe(
              tapResponse({
                next: (response: TProduct[]) => {
                  patchState(
                    store,
                    setEntities(response, configProduct),
                    setPropLoaded('loadAllProducts')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadAllProducts', error));
                },
              })
            );
          })
        )
      ),

      addProduct: rxMethod<Partial<TProduct>>(
        pipe(
          switchMap((newProduct: Partial<TProduct>) => {
            patchState(store, setPropLoading('addProduct'));
            delete newProduct?.ProductId;
            return subscriptionService.addProduct(newProduct).pipe(
              tapResponse({
                next: (response: TProduct) => {
                  patchState(
                    store,
                    addEntity(response, configProduct),
                    setPropLoaded('addProduct')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('addProduct', error));
                },
              })
            );
          })
        )
      ),

      updateProduct: rxMethod<Partial<TProduct>>(
        pipe(
          switchMap((productData: Partial<TProduct>) => {
            patchState(store, setPropLoading('updateProduct'));
            return subscriptionService.updateProduct(productData).pipe(
              tapResponse({
                next: (response: TProduct) => {
                  patchState(
                    store,
                    updateEntity(
                      { id: response.ProductId, changes: response },
                      configProduct
                    ),
                    setPropLoaded('updateProduct')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('updateProduct', error));
                },
              })
            );
          })
        )
      ),

      removeProduct: rxMethod<string>(
        pipe(
          switchMap((id: string) => {
            patchState(store, setPropLoading('removeProduct'));
            return subscriptionService.removeProduct(id).pipe(
              tapResponse({
                next: () => {
                  patchState(
                    store,
                    removeEntity(id, configProduct),
                    setPropLoaded('removeProduct')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('removeProduct', error));
                },
              })
            );
          })
        )
      ),

      loadOneProduct: rxMethod<string>(
        pipe(
          mergeMap((id: string) => {
            patchState(store, setPropLoading('loadOneProduct'));
            return subscriptionService.getProductById(id).pipe(
              tapResponse({
                next: (response: TProduct) => {
                  patchState(
                    store,
                    addEntity(response, configProduct),
                    setPropLoaded('loadOneProduct')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadOneProduct', error));
                },
              })
            );
          })
        )
      ),

      loadProduct: rxMethod<string>(
        pipe(
          switchMap((id: string) => {
            patchState(store, setPropLoading('loadProduct'));
            return subscriptionService.getProductById(id).pipe(
              tapResponse({
                next: (response: TProduct) => {
                  patchState(store, {
                    currentProduct: response,
                    ...setPropLoaded('loadProduct'),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadProduct', error));
                },
              })
            );
          })
        )
      ),

      loadSpecialCondition: rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadSpecialCondition'))),
          switchMap((id: string) => {
            return subscriptionService.getSpecialCondition(id).pipe(
              tapResponse({
                next: (response: TSpecialCondition) => {
                  patchState(store, {
                    specialCondition: response,
                    showSpecialConditionModal: true,
                    ...setPropLoaded('loadSpecialCondition'),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(
                    store,
                    setPropError('loadSpecialCondition', error)
                  );
                },
              })
            );
          })
        )
      ),

      /**
       * Carrega uma condição especial pelo id para a coleção de entidades.
       *
       * Diferente de `loadSpecialCondition`, não mexe em `specialCondition` nem
       * abre o modal do ecrã de definições: serve a quem só precisa de exibir o
       * item — o resumo da assinatura, por exemplo, onde o campo vive numa aba
       * que pode nunca ter sido aberta. Ids já em cache não geram pedido.
       */
      loadOneSpecialCondition: rxMethod<string | null>(
        pipe(
          filter(
            (id): id is string =>
              !!id && !store.specialConditionsEntityMap()[id]
          ),
          mergeMap((id: string) => {
            patchState(store, setPropLoading('loadOneSpecialCondition'));
            return subscriptionService.getSpecialCondition(id).pipe(
              tapResponse({
                next: (response: TSpecialCondition) => {
                  patchState(
                    store,
                    addEntity(response, configSpecialCondition),
                    setPropLoaded('loadOneSpecialCondition')
                  );
                },
                // Sem alerta: a exibição do item é acessória (o resumo apenas
                // omite a linha) e um toast aqui interromperia o preenchimento
                // do formulário por algo que o utilizador não pediu.
                error: (error: HttpErrorResponse) => {
                  patchState(
                    store,
                    setPropError('loadOneSpecialCondition', error)
                  );
                },
              })
            );
          })
        )
      ),

      toggleSpecialConditionModal: (
        showSpecialConditionModal = !store.showSpecialConditionModal()
      ) => {
        patchState(store, { showSpecialConditionModal });
        if (!showSpecialConditionModal) {
          patchState(store, { specialCondition: null });
        }
      },

      addSpecialCondition: rxMethod<Partial<TSpecialCondition>>(
        pipe(
          switchMap((newSpecialCondition: Partial<TSpecialCondition>) => {
            patchState(store, setPropLoading('addSpecialCondition'));
            return subscriptionService
              .addSpecialCondition(newSpecialCondition)
              .pipe(
                tapResponse({
                  next: (response: TSpecialCondition) => {
                    patchState(
                      store,
                      addEntity(response, configSpecialCondition),
                      setPropLoaded('addSpecialCondition')
                    );
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(
                      store,
                      setPropError('addSpecialCondition', error)
                    );
                  },
                })
              );
          })
        )
      ),

      updateSpecialCondition: rxMethod<Partial<TSpecialCondition>>(
        pipe(
          switchMap((specialConditionData: Partial<TSpecialCondition>) => {
            patchState(store, setPropLoading('updateSpecialCondition'));
            return subscriptionService
              .updateSpecialCondition(specialConditionData)
              .pipe(
                tapResponse({
                  next: (response: TSpecialCondition) => {
                    patchState(
                      store,
                      updateEntity(
                        { id: response.SpecialConditionId, changes: response },
                        configSpecialCondition
                      ),
                      setPropLoaded('updateSpecialCondition')
                    );
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(
                      store,
                      setPropError('updateSpecialCondition', error)
                    );
                  },
                })
              );
          })
        )
      ),

      removeSpecialCondition: rxMethod<string>(
        pipe(
          switchMap((id: string) => {
            patchState(store, setPropLoading('removeSpecialCondition'));
            return subscriptionService.removeSpecialCondition(id).pipe(
              tapResponse({
                next: () => {
                  patchState(
                    store,
                    removeEntity(id, configSpecialCondition),
                    setPropLoaded('removeSpecialCondition')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(
                    store,
                    setPropError('removeSpecialCondition', error)
                  );
                },
              })
            );
          })
        )
      ),

      loadOneCondition: rxMethod<string>(
        pipe(
          switchMap((id: string) => {
            patchState(store, setPropLoading('loadOneCondition'));
            return subscriptionService.getConditionById(id).pipe(
              tapResponse({
                next: (response: TCondition) => {
                  patchState(store, {
                    condition: response,
                    ...setPropLoaded('loadOneCondition'),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadOneCondition', error));
                },
              })
            );
          })
        )
      ),

      loadAllConditions: rxMethod<boolean | undefined | null | void>(
        pipe(
          switchMap((refresh) => {
            patchState(store, setPropLoading('loadAllConditions'));
            const conditions = structuredClone(store?.conditionsEntities());
            const hasConditions = conditions?.length > 0;

            if (hasConditions && !refresh) {
              patchState(store, setPropLoaded('loadAllConditions'));
              return EMPTY;
            }

            return subscriptionService.getAllConditions().pipe(
              tapResponse({
                next: (response: TCondition[]) => {
                  patchState(
                    store,
                    setEntities(response, configCondition),
                    setPropLoaded('loadAllConditions')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadAllConditions', error));
                },
              })
            );
          })
        )
      ),

      addCondition: rxMethod<Partial<TCondition>>(
        pipe(
          switchMap((newCondition: Partial<TCondition>) => {
            patchState(store, setPropLoading('addCondition'));
            newCondition.DurationCount = Number(newCondition.DurationCount);
            newCondition.InstallmentCount = Number(
              newCondition.InstallmentCount
            );
            delete newCondition?.ConditionId;
            return subscriptionService.addCondition(newCondition).pipe(
              tapResponse({
                next: (response: TCondition) => {
                  patchState(
                    store,
                    addEntity(response, configCondition),
                    setPropLoaded('addCondition')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('addCondition', error));
                },
              })
            );
          })
        )
      ),

      updateCondition: rxMethod<Partial<TCondition>>(
        pipe(
          switchMap((conditionData: Partial<TCondition>) => {
            patchState(store, setPropLoading('updateCondition'));
            return subscriptionService.updateCondition(conditionData).pipe(
              tapResponse({
                next: (response: TCondition) => {
                  patchState(
                    store,
                    updateEntity(
                      { id: response.ConditionId, changes: response },
                      configCondition
                    ),
                    setPropLoaded('updateCondition')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('updateCondition', error));
                },
              })
            );
          })
        )
      ),

      removeCondition: rxMethod<string>(
        pipe(
          switchMap((id: string) => {
            patchState(store, setPropLoading('removeCondition'));
            return subscriptionService.removeCondition(id).pipe(
              tapResponse({
                next: () => {
                  patchState(
                    store,
                    removeEntity(id, configCondition),
                    setPropLoaded('removeCondition')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('removeCondition', error));
                },
              })
            );
          })
        )
      ),

      loadAllFinancialRuleSets: rxMethod<boolean | undefined | null | void>(
        pipe(
          switchMap((refresh) => {
            patchState(store, setPropLoading('loadAllFinancialRuleSets'));
            const financialRuleSets = structuredClone(
              store?.financialRuleSetsEntities()
            );
            const hasFinancialRuleSets = financialRuleSets?.length > 0;

            if (hasFinancialRuleSets && !refresh) {
              patchState(store, setPropLoaded('loadAllFinancialRuleSets'));
              return EMPTY;
            }

            return subscriptionService.getAllFinancialRuleSets().pipe(
              tapResponse({
                next: (response: TFinancialRuleSet[]) => {
                  patchState(
                    store,
                    setEntities(response, configFinancialRuleSet),
                    setPropLoaded('loadAllFinancialRuleSets')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(
                    store,
                    setPropError('loadAllFinancialRuleSets', error)
                  );
                },
              })
            );
          })
        )
      ),

      loadFinancialRuleSet: rxMethod<string>(
        pipe(
          switchMap((id: string) => {
            patchState(store, setPropLoading('loadFinancialRuleSet'));
            return subscriptionService.getFinancialRuleSetById(id).pipe(
              tapResponse({
                next: (response: TFinancialRuleSet) => {
                  patchState(store, {
                    currentFinancialRuleSet: response,
                    ...setPropLoaded('loadFinancialRuleSet'),
                  });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(
                    store,
                    setPropError('loadFinancialRuleSet', error)
                  );
                },
              })
            );
          })
        )
      ),

      addFinancialRuleSet: rxMethod<Partial<TFinancialRuleSet>>(
        pipe(
          switchMap((newFinancialRuleSet: Partial<TFinancialRuleSet>) => {
            patchState(store, setPropLoading('addFinancialRuleSet'));
            const normalized =
              normalizeFinancialRuleSetPayload(newFinancialRuleSet);
            return subscriptionService.addFinancialRuleSet(normalized).pipe(
              tapResponse({
                next: (response: TFinancialRuleSet) => {
                  patchState(
                    store,
                    {
                      financialRule: response,
                      currentFinancialRuleSet: response,
                    },
                    addEntity(response, configFinancialRuleSet),
                    setPropLoaded('addFinancialRuleSet')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('addFinancialRuleSet', error));
                },
              })
            );
          })
        )
      ),

      updateFinancialRuleSet: rxMethod<Partial<TFinancialRuleSet>>(
        pipe(
          switchMap((financialRuleSetData: Partial<TFinancialRuleSet>) => {
            patchState(store, setPropLoading('updateFinancialRuleSet'));
            const normalized =
              normalizeFinancialRuleSetPayload(financialRuleSetData);
            return subscriptionService.updateFinancialRuleSet(normalized).pipe(
              tapResponse({
                next: (response: TFinancialRuleSet) => {
                  patchState(
                    store,
                    { currentFinancialRuleSet: response },
                    updateEntity(
                      { id: response.FinancialRuleSetId, changes: response },
                      configFinancialRuleSet
                    ),
                    setPropLoaded('updateFinancialRuleSet')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  patchState(
                    store,
                    setPropError('updateFinancialRuleSet', error)
                  );
                },
              })
            );
          })
        )
      ),

      removeFinancialRuleSet: rxMethod<string>(
        pipe(
          switchMap((id: string) => {
            patchState(store, setPropLoading('removeFinancialRuleSet'));
            return subscriptionService.removeFinancialRuleSet(id).pipe(
              tapResponse({
                next: () => {
                  patchState(
                    store,
                    removeEntity(id, configFinancialRuleSet),
                    setPropLoaded('removeFinancialRuleSet')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(
                    store,
                    setPropError('removeFinancialRuleSet', error)
                  );
                },
              })
            );
          })
        )
      ),

      resetAddedFinancialRuleSet: () => {
        patchState(store, {
          financialRule: null,
          ...setPropInit('addFinancialRuleSet', 'updateAccessTime'),
        });
      },

      resetAccessTimeReqState: () => {
        patchState(store, {
          ...setPropInit(
            'loadAllAccessTimes',
            'addAccessTime',
            'updateAccessTime',
            'removeAccessTime',
            'loadOneAccessTime',
            'loadAccessTime'
          ),
        });
      },

      resetProductReqState: () => {
        patchState(store, {
          ...setPropInit(
            'loadAllProducts',
            'addProduct',
            'updateProduct',
            'removeProduct',
            'loadOneProduct',
            'loadProduct'
          ),
        });
      },

      resetConditionReqState: () => {
        patchState(store, {
          ...setPropInit(
            'loadAllConditions',
            'addCondition',
            'updateCondition',
            'removeCondition',
            'loadOneCondition'
          ),
        });
      },

      resetFinancialRuleSetReqState: () => {
        patchState(store, {
          ...setPropInit(
            'loadFinancialRuleSet',
            'updateFinancialRuleSet',
            'removeFinancialRuleSet',
            'loadAllFinancialRuleSets',
            'addFinancialRuleSet'
          ),
        });
      },

      /**
       * Clears the current access time from the store state.
       * Used when closing modals or resetting the current selection.
       */
      clearCurrentAccessTime: () => {
        patchState(store, {
          currentAccessTime: null,
          ...setPropInit('loadOneAccessTime'),
        });
      },

      clearAccessTime: () => {
        patchState(store, {
          accessTime: null,
          ...setPropInit('addAccessTime'),
        });
      },

      /**
       * Clears the current product from the store state.
       * Used when closing modals or resetting the current selection.
       */
      clearCurrentProduct: () => {
        patchState(store, { currentProduct: null });
      },

      /**
       * Clears the current condition from the store state.
       * Used when closing modals or resetting the current selection.
       */
      clearCurrentCondition: () => {
        patchState(store, { condition: null });
      },

      /**
       * Clears the current financial rule set from the store state.
       * Used when closing modals or resetting the current selection.
       */
      clearCurrentFinancialRuleSet: () => {
        patchState(store, { currentFinancialRuleSet: null });
      },

      clearAddPlan: () => {
        patchState(store, { plan: null, ...setPropInit('addPlan') });
      },
    })
  ),
  withReqState(
    'loadAllAccessTimes',
    'addAccessTime',
    'updateAccessTime',
    'removeAccessTime',
    'loadOneAccessTime',
    'loadAccessTime',
    'loadAllProducts',
    'addProduct',
    'updateProduct',
    'removeProduct',
    'loadOneProduct',
    'loadProduct',
    'loadAllConditions',
    'addCondition',
    'updateCondition',
    'removeCondition',
    'loadOneCondition',
    'loadAllPlans',
    'loadPlan',
    'loadPlanForSubscription',
    'loadAccessTimesOccupancy',
    'addPlan',
    'updatePlan',
    'removePlan',
    'loadAllFinancialRuleSets',
    'addFinancialRuleSet',
    'updateFinancialRuleSet',
    'removeFinancialRuleSet',
    'loadFinancialRuleSet',
    'loadSpecialCondition',
    'loadOneSpecialCondition',
    'addSpecialCondition',
    'updateSpecialCondition',
    'removeSpecialCondition'
  )
);

function normalizeFinancialRule(
  rule: Partial<TFinancialRule> | undefined
): Partial<TFinancialRule> | undefined {
  if (!rule) return rule;
  const normalized: Partial<TFinancialRule> = {
    ...rule,
    FixedAmount: rule.FixedAmount != null ? Number(rule.FixedAmount) : null,
    RatePerDay: rule.RatePerDay != null ? Number(rule.RatePerDay) : null,
    AmountPerDay: rule.AmountPerDay != null ? Number(rule.AmountPerDay) : null,
    ToleranceDays: rule.ToleranceDays != null ? Number(rule.ToleranceDays) : 0,
    MaxAmount: rule.MaxAmount != null ? Number(rule.MaxAmount) : null,
    AppliesOffsetDays:
      rule.AppliesOffsetDays != null ? Number(rule.AppliesOffsetDays) : 0,
  };
  if (normalized.FinancialRuleId == null || normalized.FinancialRuleId === '') {
    delete normalized.FinancialRuleId;
  }
  return normalized;
}

function normalizeAdjustmentRule(
  rule: Partial<TAdjustmentRule> | undefined
): Partial<TAdjustmentRule> | undefined {
  if (!rule) return rule;
  const normalized: Partial<TAdjustmentRule> = {
    ...rule,
    Frequency: rule.Frequency != null ? rule.Frequency : undefined,
    Amount: rule.Amount != null ? Number(rule.Amount) : 0,
    Rate: rule.Rate != null ? Number(rule.Rate) : 0,
  };
  if (!normalized.FinancialAdjustmentId) {
    delete normalized.FinancialAdjustmentId;
  }
  return normalized;
}

function normalizeCancellationRule(
  rule: Partial<TFinancialCancellationRule> | undefined
): Partial<TFinancialCancellationRule> | undefined {
  if (!rule) return rule;
  const normalized: Partial<TFinancialCancellationRule> = {
    ...rule,
    FixedAmount: rule.FixedAmount != null ? Number(rule.FixedAmount) : null,
    Rate: rule.Rate != null ? Number(rule.Rate) : null,
    AmountPerDay: rule.AmountPerDay != null ? Number(rule.AmountPerDay) : null,
    RatePerDay: rule.RatePerDay != null ? Number(rule.RatePerDay) : null,
    ToleranceDays: rule.ToleranceDays != null ? Number(rule.ToleranceDays) : 0,
    MaxAmount: rule.MaxAmount != null ? Number(rule.MaxAmount) : null,
  };
  if (!normalized.FinancialCancellationRuleId) {
    delete normalized.FinancialCancellationRuleId;
  }
  return normalized;
}

function normalizeFinancialRuleSetPayload(
  payload: Partial<TFinancialRuleSet>
): Partial<TFinancialRuleSet> {
  const normalized: Partial<TFinancialRuleSet> = {
    ...payload,
    DiscountRule: payload.DiscountRule
      ? (normalizeFinancialRule(payload.DiscountRule) as TFinancialRule)
      : null,
    InterestRule: payload.InterestRule
      ? (normalizeFinancialRule(payload.InterestRule) as TFinancialRule)
      : null,
    PenaltyRule: payload.PenaltyRule
      ? (normalizeFinancialRule(payload.PenaltyRule) as TFinancialRule)
      : null,
    AdjustmentRule: payload.AdjustmentRule
      ? (normalizeAdjustmentRule(payload.AdjustmentRule) as TAdjustmentRule)
      : null,
    CancellationRule: payload.CancellationRule
      ? (normalizeCancellationRule(
          payload.CancellationRule
        ) as TFinancialCancellationRule)
      : null,
  };
  if (
    normalized.FinancialRuleSetId == null ||
    normalized.FinancialRuleSetId === ''
  ) {
    delete normalized.FinancialRuleSetId;
  }
  return normalized;
}

function normalizePlanPayload(
  payload: Partial<TPlan>,
  mode: 'add' | 'update'
): Partial<TPlan> {
  if (!payload) return payload;

  if (payload.Cycles?.length > 0) {
    payload.Cycles.forEach((cycle) => {
      cycle.FixedInstallmentAmount =
        cycle.FixedInstallmentAmount != null
          ? Number(cycle.FixedInstallmentAmount)
          : null;
      cycle.TrialDays = cycle.TrialDays != null ? Number(cycle.TrialDays) : 0;
      cycle.MaxCountPauses =
        cycle.MaxCountPauses != null ? Number(cycle.MaxCountPauses) : 0;
      cycle.MaxCountPausedDays =
        cycle.MaxCountPausedDays != null ? Number(cycle.MaxCountPausedDays) : 0;
      cycle.DependentType =
        cycle.DependentType != null
          ? (Number(cycle.DependentType) as DependentType)
          : null;
      cycle.MaxDependentCount =
        cycle.MaxDependentCount != null
          ? Number(cycle.MaxDependentCount)
          : null;
      cycle.AdditionalChargePerDependent =
        cycle.AdditionalChargePerDependent != null
          ? Number(cycle.AdditionalChargePerDependent)
          : null;

      if (cycle.DependentType !== DependentType.Appendix) {
        cycle.AdditionalChargePerDependent = null;
      }

      if (cycle.DependentType == null) {
        cycle.MaxDependentCount = null;
        cycle.AdditionalChargePerDependent = null;
      }

      if (!cycle.CycleId) {
        delete cycle.CycleId;
      }
    });
  }

  if (payload.Resources?.length > 0) {
    payload.Resources.forEach((resource) => {
      if (resource.Count != null) {
        resource.Count = Number(resource.Count);
      }
      if (!resource.ResourceId || resource.ResourceId === '') {
        delete resource.ResourceId;
      }
    });
  }

  // `null` é "sem limite" e tem de seguir explícito: o PUT só remove o limite
  // quando recebe `null`. Campo vazio no formulário também é "sem limite".
  payload.PlansAccessesTimes?.forEach((planAccessTime) => {
    const max = planAccessTime.MaxSubscriptions as number | string | null | undefined;
    planAccessTime.MaxSubscriptions =
      max === null || max === undefined || max === '' ? null : Number(max);
  });

  if (mode === 'add') {
    if ((payload as any).PlanId === '' || (payload as any).PlanId == null) {
      delete (payload as any).PlanId;
    }

    if (payload.Cycles?.length > 0) {
      payload.Cycles.forEach((cycle) => {
        delete cycle.PlanId;
        delete cycle.CycleId;
      });
    }
  }

  if (!payload?.TaxData?.TaxDataId) delete payload?.TaxData?.TaxDataId;

  return payload;
}
